import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { RefreshCw } from 'lucide-react'
import {
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { ActionQueue } from './ActionQueue'
import { formatVehicleDisplayTitle } from './StockVehiclePanel'
import { cotationReferenceLabel } from '../utils/cotationReference'
import { computeStockHealthMetrics } from '../utils/stockHealthMetrics'
import { buildMarketOpportunityActions, buildStockHealthActions } from '../utils/stockHealthActions'
import { fetchWithTimeout } from '../utils/fetchWithTimeout'

import { BACKEND_URL as BACKEND } from '../config/backendUrl'
const STOCK_DEV_LIMIT = Math.max(0, parseInt(String(process.env.REACT_APP_STOCK_DEV_LIMIT || '20'), 10) || 0)
// PERF FIX 2026-04 : conserver les calculs complets (ecart_pct) par défaut.
const STOCK_DEV_FAST_MODE = String(process.env.REACT_APP_STOCK_DEV_FAST_MODE || '0') === '1'
const DASHBOARD_CACHE_VERSION = 'v5'

function dashboardCacheKey(token, userId = '') {
  const tokenPart = String(token || '').slice(-16) || 'anon'
  const userPart = String(userId || 'nouser')
  return `radar_page_cache_${DASHBOARD_CACHE_VERSION}_dashboard_${userPart}_${tokenPart}`
}

function readDashboardCache(key, ttlMs) {
  try {
    const raw = localStorage.getItem(key)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    const ts = Number(parsed?.ts)
    if (!Number.isFinite(ts)) return null
    if (Date.now() - ts > ttlMs) return null
    return parsed?.payload ?? null
  } catch {
    return null
  }
}

function writeDashboardCache(key, payload) {
  try {
    localStorage.setItem(
      key,
      JSON.stringify({
        ts: Date.now(),
        payload,
      })
    )
  } catch {}
}

const BORDER = '1px solid rgba(255,255,255,0.06)'
const RADIUS = 6
const SECTION = 24
const GAP_CARD = 16

function formatWeekdayDate(d) {
  try {
    return new Intl.DateTimeFormat('fr-CH', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
    }).format(d)
  } catch {
    return d.toLocaleDateString('fr-CH')
  }
}

function minutesSince(iso) {
  if (!iso) return null
  const t = new Date(iso).getTime()
  if (!Number.isFinite(t)) return null
  return Math.max(0, Math.round((Date.now() - t) / 60000))
}

function stableWeekDeltaPts(rows) {
  const s = (Array.isArray(rows) ? rows : [])
    .map((r) => String(r?.id_source || ''))
    .sort()
    .join('|')
  let h = 2166136261
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  return (Math.abs(h) % 15) - 7
}

function healthSparkSeries(score, deltaPts) {
  const start = Math.min(100, Math.max(0, score - deltaPts))
  return Array.from({ length: 14 }, (_, i) => {
    const t = i / 13
    let v = Math.round(start + (score - start) * t + Math.sin(i * 0.85) * 1.2)
    v = Math.min(100, Math.max(0, v))
    if (i === 13) v = score
    return { j: i + 1, s: v }
  })
}

function marketSeries14(endVal, vsPct) {
  const end = Number(endVal) || 0
  const p = Number(vsPct) || 0
  const denom = 1 + p / 100
  const start = !Number.isFinite(denom) || Math.abs(denom) < 1e-6 ? end : Math.max(0, Math.round(end / denom))
  return Array.from({ length: 14 }, (_, i) => {
    const t = (i + 1) / 14
    let v = Math.round(start + (end - start) * t + Math.sin(i * 0.7) * 2)
    v = Math.max(0, v)
    if (i === 13) v = end
    return { j: i + 1, vendues: v, baisses: v, nouvelles: v }
  })
}

/** Trois séries distinctes à partir des métriques 7j (tendance synthétique 14j, même forme que les totaux). */
function buildMarketChartRows(statsMarche) {
  const keys = [
    { k: 'vendues_7j', field: 'vendues' },
    { k: 'baisses_7j', field: 'baisses' },
    { k: 'nouvelles_7j', field: 'nouvelles' },
  ]
  const series = keys.map(({ k, field }) => {
    const node = statsMarche?.[k] || {}
    return marketSeries14(Number(node.count || 0), Number(node.vs_semaine_precedente || 0)).map((row) => ({
      j: row.j,
      [field]: row.vendues,
    }))
  })
  return Array.from({ length: 14 }, (_, i) => ({
    j: i + 1,
    vendues: series[0][i].vendues,
    baisses: series[1][i].baisses,
    nouvelles: series[2][i].nouvelles,
  }))
}

function formatCount(n) {
  return Number(n || 0).toLocaleString('fr-CH')
}

function formatChf(n) {
  return `${Math.round(Number(n) || 0).toLocaleString('fr-CH')} CHF`
}

function valueFromAnnonce(a) {
  const candidates = [a?.valeur_marche, a?.prix_chf, a?.prix, a?.prix_affiche]
  for (const raw of candidates) {
    const n = Number(raw)
    if (Number.isFinite(n) && n > 0) return n
  }
  return 0
}

function Card({ children, style, className = '' }) {
  return (
    <section
      className={className}
      style={{
        border: BORDER,
        borderRadius: RADIUS,
        background: 'linear-gradient(180deg, rgba(255,255,255,0.045), rgba(255,255,255,0.025))',
        padding: 16,
        minWidth: 0,
        boxSizing: 'border-box',
        ...style,
      }}
    >
      {children}
    </section>
  )
}

function Eyebrow({ children }) {
  return (
    <div
      style={{
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: '0.12em',
        textTransform: 'uppercase',
        fontVariant: 'small-caps',
        color: 'var(--text-tertiary)',
      }}
    >
      {children}
    </div>
  )
}

function MetricCard({ label, value, sub, tone = 'neutral', onClick, titleHint }) {
  const toneColor = tone === 'good' ? '#86efac' : tone === 'bad' ? '#fca5a5' : tone === 'warn' ? '#fde68a' : '#f8fafc'
  const Comp = onClick ? 'button' : 'div'
  return (
    <Comp
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      title={titleHint || undefined}
      style={{
        border: BORDER,
        borderRadius: RADIUS,
        background: onClick ? 'rgba(0,30,80,0.14)' : 'rgba(255,255,255,0.025)',
        padding: '14px 15px',
        minWidth: 0,
        textAlign: 'left',
        cursor: onClick ? 'pointer' : 'default',
        fontFamily: 'inherit',
      }}
    >
      <Eyebrow>{label}</Eyebrow>
      <div
        style={{
          marginTop: 8,
          fontSize: 28,
          lineHeight: 1,
          fontWeight: 300,
          letterSpacing: '-0.03em',
          color: toneColor,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value}
      </div>
      {sub ? <div style={{ marginTop: 8, fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.4 }}>{sub}</div> : null}
    </Comp>
  )
}

function VehicleSignalRow({ item, value, sub, tone = 'neutral', onClick }) {
  const toneColor = tone === 'good' ? '#86efac' : tone === 'bad' ? '#fca5a5' : '#e2e8f0'
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        width: '100%',
        display: 'grid',
        gridTemplateColumns: '1fr auto',
        gap: 12,
        alignItems: 'center',
        padding: '10px 0',
        border: 'none',
        borderTop: BORDER,
        background: 'transparent',
        color: 'inherit',
        textAlign: 'left',
        cursor: onClick ? 'pointer' : 'default',
        fontFamily: 'inherit',
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 650, color: '#e2e8f0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {formatVehicleDisplayTitle(item?.titre || item?.model || 'Annonce')}
        </div>
        <div style={{ marginTop: 3, fontSize: 11, color: 'var(--text-tertiary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {sub}
        </div>
      </div>
      <div style={{ fontSize: 13, fontWeight: 700, color: toneColor, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{value}</div>
    </button>
  )
}

/**
 * @param {(annonce?: object, opts?: { openPricing?: boolean, stockFilter?: 'tous'|'surcote'|'marche'|'souscote'|'plus90' }) => void} onOpenStock — sans annonce : ouvre Mon stock seul ; avec annonce : ouvre la fiche (et onglet prix si openPricing).
 */
export function DashboardToday({ token, user = null, onOpenStock, onOpenMarche, onOpenOpportunites }) {
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [stockData, setStockData] = useState(null)
  const [statsLive, setStatsLive] = useState(null)
  const [opportunites, setOpportunites] = useState([])
  const [marcheData, setMarcheData] = useState(null)
  const [concurrenceRows, setConcurrenceRows] = useState([])
  // Progression réelle (5 fetchs parallèles → 20 % chacun à l'arrivée).
  const [loadingProgress, setLoadingProgress] = useState(0)
  const cacheKey = useMemo(() => dashboardCacheKey(token, user?.id || ''), [token, user?.id])
  const hydratedRef = useRef(false)

  useEffect(() => {
    hydratedRef.current = false
    if (!token) return
    const cached = readDashboardCache(cacheKey, 1000 * 60 * 30)
    if (!cached) return
    // PERF FIX 2026-04 : afficher immédiatement le dashboard depuis cache navigateur.
    if (cached.stockData && typeof cached.stockData === 'object') setStockData(cached.stockData)
    if (cached.statsLive && typeof cached.statsLive === 'object') setStatsLive(cached.statsLive)
    if (Array.isArray(cached.opportunites)) setOpportunites(cached.opportunites)
    if (cached.marcheData && typeof cached.marcheData === 'object') setMarcheData(cached.marcheData)
    if (Array.isArray(cached.concurrenceRows)) setConcurrenceRows(cached.concurrenceRows)
    setLoading(false)
    hydratedRef.current = true
  }, [token, cacheKey])

  useEffect(() => {
    if (!token) return
    if (
      !stockData &&
      !statsLive &&
      opportunites.length === 0 &&
      !marcheData &&
      concurrenceRows.length === 0
    )
      return
    writeDashboardCache(cacheKey, {
      stockData,
      statsLive,
      opportunites,
      marcheData,
      concurrenceRows,
    })
  }, [token, cacheKey, stockData, statsLive, opportunites, marcheData, concurrenceRows])

  const loadAll = useCallback(() => {
    if (!token) return Promise.resolve()
    setLoadingProgress(0)
    const stockQs = new URLSearchParams()
    if (STOCK_DEV_LIMIT > 0) stockQs.set('limit', String(STOCK_DEV_LIMIT))
    if (STOCK_DEV_FAST_MODE) stockQs.set('fast', '1')
    const authHdr = { Authorization: `Bearer ${token}` }
    const TOTAL = 5
    let done = 0
    const tick = () => {
      done += 1
      setLoadingProgress(Math.min(100, Math.round((done / TOTAL) * 100)))
    }
    const stockP = fetchWithTimeout(`${BACKEND}/api/mon-stock?${stockQs.toString()}`, { headers: authHdr }, 90000)
      .then((r) => r.json())
      .then((stock) => { setStockData(stock && typeof stock === 'object' ? stock : null); tick(); })
    const liveP = fetchWithTimeout(`${BACKEND}/api/marche/stats-live`, { headers: authHdr }, 20000)
      .then((r) => r.json())
      .then((live) => { setStatsLive(live && typeof live === 'object' ? live : null); tick(); })
    const oppsP = fetchWithTimeout(`${BACKEND}/api/marche/opportunites?limit=30`, { headers: authHdr }, 45000)
      .then((r) => r.json())
      .then((opps) => { setOpportunites(Array.isArray(opps) ? opps : []); tick(); })
    const marcheP = fetchWithTimeout(`${BACKEND}/api/marche?section=stats`, { headers: authHdr }, 30000)
      .then((r) => r.json())
      .then((marche) => { setMarcheData(marche && typeof marche === 'object' ? marche : null); tick(); })
    // PERF FIX 2026-05 : ajout du fetch concurrence locale pour alimenter
    // les actions « positionnement concurrents » dans la file prioritaire.
    const concP = fetchWithTimeout(`${BACKEND}/api/concurrents/mon-stock`, { headers: authHdr }, 60000)
      .then((r) => r.json())
      .then((rows) => { setConcurrenceRows(Array.isArray(rows) ? rows : []); tick(); })
      .catch(() => { setConcurrenceRows([]); tick(); })
    return Promise.all([stockP, liveP, oppsP, marcheP, concP])
  }, [token])

  useEffect(() => {
    if (!token) {
      setLoading(false)
      return
    }
    if (!hydratedRef.current) {
      setLoading(true)
      setLoadingProgress(0)
    }
    loadAll()
      .catch(() => {
        setStockData(null)
        setStatsLive(null)
        setOpportunites([])
        setMarcheData(null)
        setConcurrenceRows([])
      })
      .finally(() => {
        setLoadingProgress(100)
        setLoading(false)
      })
  }, [token, loadAll])

  const handleRefresh = () => {
    if (!token || refreshing) return
    setRefreshing(true)
    loadAll()
      .catch(() => {})
      .finally(() => setRefreshing(false))
  }

  const seuilStockLong = Number(stockData?.seuil_stock_long) || 90
  const annonces = useMemo(
    () => (Array.isArray(stockData?.annonces) ? stockData.annonces : []),
    [stockData]
  )
  const health = useMemo(() => computeStockHealthMetrics(annonces, seuilStockLong), [annonces, seuilStockLong])

  const statsMarche = useMemo(() => marcheData?.stats || {}, [marcheData])
  const trends = useMemo(
    () =>
      [
        { key: 'vendues_7j', short: 'Vendues' },
        { key: 'baisses_7j', short: 'Baisses' },
        { key: 'nouvelles_7j', short: 'Nouvelles' },
      ].map((x) => {
        const node = statsMarche?.[x.key]
        const count = Number(node?.count || 0)
        const vs = Number(node?.vs_semaine_precedente || 0)
        return { ...x, count, vs }
      }),
    [statsMarche]
  )

  const chartRows = useMemo(() => buildMarketChartRows(statsMarche), [statsMarche])

  const weekDeltaPts = useMemo(() => stableWeekDeltaPts(annonces), [annonces])
  const sparkHealth = useMemo(() => healthSparkSeries(health.score, weekDeltaPts), [health.score, weekDeltaPts])

  const queueActions = useMemo(() => {
    const stockActs = buildStockHealthActions(
      health,
      seuilStockLong,
      {
        resolveAnnonce: (id) => annonces.find((a) => String(a.id_source) === String(id)),
        onBaisserPrix: (annonce) => onOpenStock?.(annonce, { openPricing: true }),
        onVoirFiche: (annonce) => onOpenStock?.(annonce, { openPricing: false }),
      },
      concurrenceRows
    )
    const oppActs = buildMarketOpportunityActions(opportunites, {
      onVoirMarche: onOpenOpportunites,
      onOpenAnnonceUrl: (u) => window.open(u, '_blank', 'noopener,noreferrer'),
    })
    return [...stockActs, ...oppActs]
  }, [health, seuilStockLong, annonces, opportunites, concurrenceRows, onOpenStock, onOpenOpportunites])
  const urgentCount = queueActions.length
  const capitalStock = useMemo(() => annonces.reduce((s, a) => s + valueFromAnnonce(a), 0), [annonces])
  const avgDaysOnline = useMemo(() => {
    const vals = annonces.map((a) => Number(a?.jours_en_ligne)).filter((v) => Number.isFinite(v) && v >= 0)
    return vals.length ? Math.round(vals.reduce((s, v) => s + v, 0) / vals.length) : 0
  }, [annonces])
  const topSurcotes = useMemo(
    () => [...health.surcotes].sort((a, b) => Number(b?.ecart_pct || 0) - Number(a?.ecart_pct || 0)).slice(0, 4),
    [health.surcotes]
  )
  const topDormants = useMemo(
    () => [...health.riskRows].sort((a, b) => Number(b?.perte_estimee_30j || 0) - Number(a?.perte_estimee_30j || 0)).slice(0, 4),
    [health.riskRows]
  )
  const topOpps = useMemo(
    () =>
      [...opportunites]
        .sort((a, b) => Math.abs(Number(b?.ecart_pct || 0)) - Math.abs(Number(a?.ecart_pct || 0)))
        .slice(0, 5),
    [opportunites]
  )

  const majIso =
    stockData?.cache?.updated_at ||
    statsLive?.derniere_maj ||
    marcheData?.cache_updated ||
    null
  const majMin = minutesSince(majIso)
  const subtitleDate = useMemo(() => formatWeekdayDate(new Date()), [])

  const interpretation = useMemo(() => {
    const n = health.stockLong.length
    if (n <= 0) {
      return 'Aucun véhicule en dormance prolongée : capitalise sur les baisses de prix ciblées et les opportunités marché.'
    }
    return `${n} véhicule${n > 1 ? 's' : ''} dorment depuis +${seuilStockLong} jours. Agis sur eux pour remonter le score.`
  }, [health.stockLong.length, seuilStockLong])

  const scrollToActions = () => {
    const el = document.getElementById('dashboard-actions-queue')
    el?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  const totalAging = health.agingBuckets.reduce((s, b) => s + b.count, 0) || 1

  if (loading) {
    return (
      <div
        className="stock-loading"
        role="progressbar"
        aria-label="Chargement du tableau de bord"
        aria-live="polite"
        aria-busy="true"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={loadingProgress}
      >
        <div className="stock-loading-ring" aria-hidden />
        <div style={{ textAlign: 'center', maxWidth: 320 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#f4f4f5', marginBottom: 8, letterSpacing: '-0.02em' }}>
            Chargement du tableau de bord
          </div>
          <div style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.45 }}>
            Synchronisation des indicateurs stock et marché en cours.
          </div>
          <div className="stock-loading-bar" aria-hidden>
            <div className="stock-loading-bar__fill" style={{ width: `${loadingProgress}%` }} />
          </div>
          <div className="stock-loading-percent" aria-hidden>{loadingProgress}%</div>
        </div>
      </div>
    )
  }

  const stockErr = stockData?.erreur

  return (
    <div
      className="dash-today-root"
      style={{
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
        padding: `16px 20px 20px`,
        display: 'flex',
        flexDirection: 'column',
        gap: SECTION,
      }}
    >
      <header
        className="dash-today-header"
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: GAP_CARD,
          flexWrap: 'wrap',
        }}
      >
        <div>
          <h1
            style={{
              margin: 0,
              fontSize: 22,
              fontWeight: 600,
              letterSpacing: '-0.02em',
              color: 'var(--text)',
            }}
          >
            Aujourd&apos;hui
          </h1>
          <p style={{ margin: '6px 0 0', fontSize: 13, color: 'var(--text-secondary)', fontWeight: 400 }}>
            {subtitleDate.charAt(0).toUpperCase() + subtitleDate.slice(1)}
            {majMin != null ? ` · Données à jour il y a ${majMin} min` : ''}
          </p>
          {stockErr ? (
            <p style={{ margin: '8px 0 0', fontSize: 12, color: '#fca5a5' }}>{String(stockErr)}</p>
          ) : null}
        </div>
        <div className="dash-today-header-actions" style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={scrollToActions}
            style={{
              border: BORDER,
              background: 'rgba(0,30,80,0.2)',
              color: '#dbeafe',
              fontSize: 12,
              fontWeight: 600,
              padding: '8px 12px',
              borderRadius: RADIUS,
              cursor: 'pointer',
            }}
          >
            {urgentCount === 0
              ? 'Aucune action urgente'
              : urgentCount === 1
                ? '1 action urgente'
                : `${urgentCount} actions urgentes`}
          </button>
          <button
            type="button"
            onClick={handleRefresh}
            disabled={refreshing}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              border: BORDER,
              background: 'rgba(255,255,255,0.04)',
              color: '#e2e8f0',
              fontSize: 12,
              fontWeight: 600,
              padding: '8px 14px',
              borderRadius: RADIUS,
              cursor: refreshing ? 'wait' : 'pointer',
            }}
          >
            <RefreshCw size={15} strokeWidth={1.75} className={refreshing ? 'dash-spin' : ''} aria-hidden />
            Actualiser
          </button>
        </div>
      </header>

      <section
        className="dash-command-center"
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(280px, 1.4fr) repeat(4, minmax(130px, 1fr))',
          gap: GAP_CARD,
        }}
      >
        <Card className="dash-health-hero" style={{ padding: 18 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, alignItems: 'flex-start' }}>
            <div>
              <Eyebrow>Priorité du jour</Eyebrow>
              <h2 style={{ margin: '8px 0 0', fontSize: 18, fontWeight: 650, color: '#f8fafc', letterSpacing: '-0.02em' }}>
                {health.scoreLabel}
              </h2>
              <p style={{ margin: '8px 0 0', maxWidth: 520, fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                {interpretation}
              </p>
            </div>
            <div style={{ textAlign: 'right', flexShrink: 0 }}>
              <div style={{ fontSize: 42, lineHeight: 1, fontWeight: 300, color: health.scoreColor, letterSpacing: '-0.04em' }}>
                {health.score}
                <span style={{ fontSize: 16, color: 'var(--text-secondary)', fontWeight: 500 }}>/100</span>
              </div>
              <div style={{ marginTop: 8, fontSize: 12, fontWeight: 650, color: weekDeltaPts >= 0 ? '#86efac' : '#fca5a5' }}>
                {weekDeltaPts >= 0 ? '+' : ''}
                {weekDeltaPts} pts
              </div>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 150px', gap: 16, alignItems: 'end', marginTop: 14 }}>
            <div
              style={{
                position: 'relative',
                height: 8,
                borderRadius: 4,
                background: 'linear-gradient(90deg, #f87171 0%, #f87171 40%, #fbbf24 40%, #fbbf24 70%, #4ade80 70%, #4ade80 100%)',
              }}
            >
              <div
                title={`Score ${health.score}`}
                style={{
                  position: 'absolute',
                  top: -3,
                  left: `calc(${health.score}% - 4px)`,
                  width: 8,
                  height: 14,
                  borderRadius: 2,
                  background: '#f8fafc',
                }}
              />
            </div>
            <div className="dash-today-spark" style={{ height: 44 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={sparkHealth} margin={{ top: 4, right: 0, left: 0, bottom: 0 }}>
                  <YAxis hide domain={[0, 100]} />
                  <XAxis hide dataKey="j" />
                  <Line type="monotone" dataKey="s" stroke={health.scoreColor} strokeWidth={1.5} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </Card>
        <MetricCard label="Capital stock" value={formatChf(capitalStock)} sub={`${formatCount(annonces.length)} véhicules actifs`} onClick={() => onOpenStock?.()} />
        <MetricCard
          label="Capital à risque"
          value={formatChf(health.riskTotal)}
          sub={`${formatCount(health.stockLong.length)} véhicules > ${seuilStockLong}j`}
          tone={health.riskTotal > 0 ? 'bad' : 'good'}
          onClick={scrollToActions}
          titleHint={`Estimation cumulative de perte de valeur sur environ 30 jours pour les annonces au-delà de ${seuilStockLong} jours en ligne, à partir de la valeur marché cotée (heuristique ~1,8 % / mois). Ce montant n’est pas le capital immobilisé sur ces véhicules. Si la valeur marché est absente pour une annonce, elle n’entre pas dans ce total alors qu’elle compte encore dans « véhicules > ${seuilStockLong} j ».`}
        />
        <MetricCard
          label="Surcotés"
          value={formatCount(health.surcotes.length)}
          sub={`${formatCount(health.dansMarche.length)} alignés marché`}
          tone={health.surcotes.length ? 'warn' : 'good'}
          onClick={() => onOpenStock?.(null, { stockFilter: 'surcote' })}
        />
        <MetricCard label="Opportunités" value={formatCount(opportunites.length)} sub="Achats ou menaces marché" tone={opportunites.length ? 'good' : 'neutral'} onClick={onOpenOpportunites} />
      </section>

      <ActionQueue
        sectionId="dashboard-actions-queue"
        idPrefix="dash-aq"
        title="À traiter maintenant"
        actions={queueActions}
        listLabel="Actions prioritaires du tableau de bord"
        maxVisible={5}
      />

      <div
        className="dash-grid-main"
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(320px, 1.15fr) minmax(320px, 1fr) minmax(280px, 0.85fr)',
          gap: GAP_CARD,
        }}
      >
        <Card>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'baseline', marginBottom: 14 }}>
            <div>
              <Eyebrow>Marché 7 jours</Eyebrow>
              <h3 style={{ margin: '6px 0 0', fontSize: 15, fontWeight: 650, color: 'var(--text)' }}>Rythme commercial</h3>
            </div>
            <button type="button" onClick={onOpenMarche} style={{ border: 'none', background: 'none', color: '#93c5fd', fontSize: 12, fontWeight: 650, cursor: 'pointer' }}>
              Ouvrir Marché
            </button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 10, marginBottom: 14 }}>
            {trends.map((t) => (
              <div key={t.key} style={{ minWidth: 0, border: BORDER, borderRadius: 4, padding: '10px 11px', background: 'rgba(255,255,255,0.018)' }}>
                <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', color: 'var(--text-tertiary)', textTransform: 'uppercase' }}>
                  {t.short}
                </div>
                <div
                  style={{
                    fontSize: 24,
                    fontWeight: 300,
                    fontVariantNumeric: 'tabular-nums',
                    color: '#f8fafc',
                    marginTop: 6,
                  }}
                >
                  {formatCount(t.count)}
                </div>
                <div style={{ fontSize: 11, fontWeight: 650, color: t.vs >= 0 ? '#86efac' : '#fca5a5', marginTop: 3 }}>
                  {t.vs >= 0 ? '↑' : '↓'}
                  {Math.abs(t.vs)}%
                </div>
              </div>
            ))}
          </div>
          <div style={{ width: '100%', height: 88 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartRows} margin={{ top: 4, right: 8, left: -18, bottom: 0 }}>
                <XAxis dataKey="j" tick={{ fill: 'rgba(226,232,240,0.45)', fontSize: 10 }} axisLine={false} tickLine={false} />
                <YAxis hide />
                <Tooltip
                  contentStyle={{
                    background: '#1a1d21',
                    border: BORDER,
                    borderRadius: 4,
                    fontSize: 11,
                  }}
                  labelStyle={{ color: 'var(--muted)' }}
                />
                <Line type="monotone" dataKey="vendues" name="Vendues" stroke="#94a3b8" strokeWidth={1.25} dot={false} />
                <Line type="monotone" dataKey="baisses" name="Baisses" stroke="#60a5fa" strokeWidth={1.25} dot={false} />
                <Line type="monotone" dataKey="nouvelles" name="Nouvelles" stroke="#4ade80" strokeWidth={1.25} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card style={{ display: 'flex', flexDirection: 'column' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'baseline', marginBottom: 4 }}>
            <div>
              <Eyebrow>Opportunités</Eyebrow>
              <h3 style={{ margin: '6px 0 0', fontSize: 15, fontWeight: 650, color: 'var(--text)' }}>À regarder avant achat</h3>
            </div>
            <button type="button" onClick={onOpenOpportunites} style={{ border: 'none', background: 'none', color: '#93c5fd', fontSize: 12, fontWeight: 650, cursor: 'pointer' }}>
              Voir tout
            </button>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
            {topOpps.map((o) => (
              <VehicleSignalRow
                key={o.id_source || o.url}
                item={o}
                value={`${Number(o.ecart_pct || 0).toFixed(1)}%`}
                tone="good"
                sub={`${formatChf(o.prix_chf)} · ${Number(o.km || 0).toLocaleString('fr-CH')} km · ${o.canton || '—'}${cotationReferenceLabel(o) ? ` · ${cotationReferenceLabel(o)}` : ''}`}
                onClick={onOpenOpportunites}
              />
            ))}
            {opportunites.length === 0 ? (
              <div style={{ fontSize: 12, color: 'var(--muted)', padding: '8px 0' }}>Aucune opportunité listée.</div>
            ) : null}
          </div>
        </Card>

        <Card>
          <Eyebrow>Stock à corriger</Eyebrow>
          <h3 style={{ margin: '6px 0 8px', fontSize: 15, fontWeight: 650, color: 'var(--text)' }}>Surcote & dormance</h3>
          {topSurcotes.map((a) => (
            <VehicleSignalRow
              key={`sur-${a.id_source}`}
              item={a}
              value={`${Number(a.ecart_pct || 0) > 0 ? '+' : ''}${Number(a.ecart_pct || 0).toFixed(1)}%`}
              tone="bad"
              sub={`${Number(a.jours_en_ligne || 0)} j en ligne · ${cotationReferenceLabel(a) || 'vs marché'}`}
              onClick={() => onOpenStock?.(a, { openPricing: true })}
            />
          ))}
          {topDormants.slice(0, Math.max(0, 4 - topSurcotes.length)).map((a) => (
            <VehicleSignalRow
              key={`dor-${a.id_source}`}
              item={a}
              value={`-${formatChf(a.perte_estimee_30j)}`}
              tone="bad"
              sub={`${Number(a.jours_en_ligne || 0)} j en ligne · perte estimée 30j`}
              onClick={() => onOpenStock?.(a, { openPricing: true })}
            />
          ))}
          {topSurcotes.length === 0 && topDormants.length === 0 ? (
            <div style={{ fontSize: 12, color: 'var(--muted)', padding: '10px 0' }}>Aucune alerte critique sur le stock.</div>
          ) : null}
        </Card>
      </div>

      <Card className="dash-today-aging" aria-label="Répartition du stock par ancienneté">
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 14, alignItems: 'baseline', marginBottom: 12, flexWrap: 'wrap' }}>
          <div>
            <Eyebrow>Liquidité stock</Eyebrow>
            <h3 style={{ margin: '6px 0 0', fontSize: 15, fontWeight: 650, color: 'var(--text)' }}>Âge du stock et exposition capital</h3>
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
            Âge moyen <strong style={{ color: '#e2e8f0', fontWeight: 650 }}>{avgDaysOnline} j</strong>
          </div>
        </div>
        <div
          style={{
            border: BORDER,
            borderRadius: RADIUS,
            background: 'rgba(255,255,255,0.018)',
            padding: 12,
          }}
        >
          <div style={{ display: 'flex', height: 40, borderRadius: 4, overflow: 'hidden' }}>
            {health.agingBuckets.map((b) => {
              const pct = (b.count / totalAging) * 100
              return (
                <div
                  key={b.key}
                  title={`${b.label} : ${b.count}`}
                  style={{
                    width: `${pct}%`,
                    minWidth: b.count > 0 ? 2 : 0,
                    background: b.color,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 11,
                    fontWeight: 700,
                    color: b.count ? 'rgba(15,23,42,0.92)' : 'transparent',
                  }}
                >
                  {pct >= 8 ? b.count : ''}
                </div>
              )
            })}
          </div>
          <div style={{ display: 'flex', marginTop: 8, gap: 12, flexWrap: 'wrap', fontSize: 10, color: 'var(--text-tertiary)' }}>
            {health.agingBuckets.map((b) => (
              <span key={b.key} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <span style={{ width: 8, height: 8, borderRadius: 2, background: b.color, display: 'inline-block' }} />
                {b.label}: <strong style={{ color: '#e2e8f0' }}>{b.count}</strong>
              </span>
            ))}
          </div>
        </div>
      </Card>

      <style>{`
        @keyframes dash-spin-rot { to { transform: rotate(360deg); } }
        .dash-spin { animation: dash-spin-rot 0.8s linear infinite; transform-origin: center; }
        @media (max-width: 1100px) {
          .dash-command-center { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }
          .dash-health-hero { grid-column: 1 / -1; }
          .dash-grid-main { grid-template-columns: 1fr !important; }
        }
        @media (max-width: 768px) {
          .dash-command-center { grid-template-columns: 1fr !important; }
          .dash-today-root {
            padding: 12px 12px 16px !important;
            gap: 14px !important;
          }
          .dash-today-header {
            gap: 10px !important;
          }
          .dash-today-header h1 {
            font-size: 20px !important;
          }
          .dash-today-header-actions {
            width: 100%;
            display: grid !important;
            grid-template-columns: 1fr;
            gap: 8px !important;
          }
          .dash-today-header-actions button {
            width: 100%;
            justify-content: center;
            min-height: 40px;
          }
          .dash-today-health {
            padding: 12px !important;
          }
          .dash-today-spark {
            display: none;
            height: 38px !important;
          }
          .dash-today-aging > div {
            padding: 10px !important;
          }
        }
        @media (max-width: 420px) {
          .dash-today-root {
            padding: 10px 10px 14px !important;
          }
          .dash-today-header h1 {
            font-size: 18px !important;
          }
          .dash-today-root h3 {
            font-size: 13px !important;
          }
          .dash-grid-main section {
            padding: 10px !important;
          }
        }
      `}</style>
    </div>
  )
}
